import React, { useState, useEffect, useRef } from 'react';
import ChatHistory from './ChatHistory';
import MessageInput from './MessageInput';
import { generateSessionId, getSessionId, clearSessionId } from '../utils/sessionManager';
import { sendMessage } from '../utils/api';
import './ChatInterface.css';
import ReCAPTCHA from "react-google-recaptcha";

const ChatInterface = ({ selectedSource, activeTab, onTabChange, onReset}) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef();

  const sources = [
    { id: 'all', name: '', color: 'transparent', label: 'All of\nIntellyx' },  // No background, no text
    { id: 'JB', name: '', color: '#e74c3c', label: 'Jason\nBloomberg' },  // Complementary Red
    { id: 'JE', name: '', color: '#1E5BA8', label: 'Jason\nEnglish' },  // Intellyx Blue
    { id: 'EN', name: '', color: '#2ecc71', label: 'Eric\nNewcomer' }   // Complementary Green
  ];

  
 


  useEffect(() => {
    // Generate or get session ID for the active tab
    const currentSessionId = getSessionId(activeTab);
    setSessionId(currentSessionId);
    
    // Load messages for this session from localStorage
    const savedMessages = localStorage.getItem(`messages_${activeTab}_${currentSessionId}`);
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      setMessages([]);
    }
  
  }, [activeTab]);

  const getBackgroundImage = (sourceId) => {
    const imageMap = {
      all: `${process.env.PUBLIC_URL}/images/intellyx-logo.png`,
      JE: `${process.env.PUBLIC_URL}/images/JE.jpg`,
      JB: `${process.env.PUBLIC_URL}/images/JB.jpg`,
      EN: `${process.env.PUBLIC_URL}/images/EN.jpg`
    };
    return imageMap[sourceId] || '';
  };

  const handleAuthorClick = (authorId) => {    
    if (authorId !== activeTab) {
      // Switch to different author - load existing conversation if it exists
      onTabChange(authorId);
    }
  };

  const handleSendMessage = async (message) => {
    const userMessage = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setIsLoading(true);

    // Save messages to localStorage
    localStorage.setItem(`messages_${activeTab}_${sessionId}`, JSON.stringify(newMessages));

    try {
      let token = null;
      
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.reset();
          await new Promise(resolve => setTimeout(resolve, 100));
          token = await recaptchaRef.current.executeAsync();
          console.log('ReCAPTCHA token obtained:', token ? 'success' : 'failed');
        } catch (recaptchaError) {
          console.error('ReCAPTCHA error:', recaptchaError);
          // Continue without token - let the backend handle it
        }
      }
    
      const apiResponse = await sendMessage(message, sessionId, activeTab, token);
      

      if (activeTab === 'all' && apiResponse.responses && Array.isArray(apiResponse.responses)) {
        // Handle multiple responses for "All" tab
        const sortedResponses = [...apiResponse.responses].sort((a, b) => {
          // Calculate similarity sum for response a
          const aSimilaritySum = a.documents && a.documents.length > 0 
            ? a.documents.reduce((sum, doc) => sum + (doc.similarity || 0), 0)
            : 0;
          
          // Calculate similarity sum for response b  
          const bSimilaritySum = b.documents && b.documents.length > 0
            ? b.documents.reduce((sum, doc) => sum + (doc.similarity || 0), 0)
            : 0;
          
          // Sort by similarity sum in descending order (highest first)
          return bSimilaritySum - aSimilaritySum;
        });

        const botMessage = {
          id: Date.now() + 1,
          sender: 'bot',
          timestamp: new Date(),
          multipleResponses: sortedResponses
        };

        const updatedMessages = [...newMessages, botMessage];
        setMessages(updatedMessages);
        
        // Save updated messages to localStorage
        localStorage.setItem(`messages_${activeTab}_${sessionId}`, JSON.stringify(updatedMessages));
      } else {
        // Handle single response for individual author tabs
        const botMessage = {
          id: Date.now() + 1,
          text: apiResponse.text,
          sender: 'bot',
          timestamp: new Date(),
          documents: apiResponse.documents || []
        };

        const updatedMessages = [...newMessages, botMessage];
        setMessages(updatedMessages);
        
        // Save updated messages to localStorage
        localStorage.setItem(`messages_${activeTab}_${sessionId}`, JSON.stringify(updatedMessages));
      }
    } catch (error) {
      console.error('Message sending error:', error);
      console.error('Error stack:', error.stack);
      
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, there was an error processing your message. Please try again.',
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };

      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);
      
      localStorage.setItem(`messages_${activeTab}_${sessionId}`, JSON.stringify(updatedMessages));
    } finally {
      setIsLoading(false);
    }
  };

 
  return (
    <div className="chat-interface">
      {/* Author Circles Header */}
      <div className="author-circles-header">
        <div className="author-circles">
          {sources.map((source) => (
            <div key={source.id} className="author-container"  onClick={() => handleAuthorClick(source.id)}>
              <div
                className={`author-circle author-${source.id} ${activeTab === source.id ? 'active' : ''}`}
                style={{ 
                  backgroundColor: source.color,
                  backgroundImage: `url(${getBackgroundImage(source.id)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: source.id === 'all' ? 'left center' : 'center',
                  backgroundRepeat: 'no-repeat'
                }}
               
                title={`Chat with ${source.label}`}
              >
                {source.name}
              </div>
              <div className="author-label-text" >{source.label}</div>
            </div>
          ))}
        </div>
        <button className="reset-btn" onClick={onReset} title="Reset to source selection">
          ↺
        </button>
      </div>

      {/* Chat Content */}
      <div className="chat-content">
        <ChatHistory messages={messages} isLoading={isLoading} activeTab={activeTab} />
        <MessageInput onSendMessage={handleSendMessage} disabled={isLoading} />
      </div>  
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey="6LeQVp4rAAAAADLl0zR2bdn1stDGNE9UFhkeOzSE"
        size="invisible"
        badge="inline"
      />
      
      {/* Custom reCAPTCHA Badge */}
      <div className="custom-recaptcha-badge">
        <div className="recaptcha-compact">
          <img 
            src="https://www.gstatic.com/recaptcha/api2/logo_48.png" 
            alt="reCAPTCHA" 
            className="recaptcha-logo"
          />
        </div>
        <div className="recaptcha-expanded">
          <div className="recaptcha-text">
            <span>This site is protected by reCAPTCHA and the Google </span>
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <span> and </span>
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            <span> apply.</span>
          </div>
        </div>
      </div>  
    </div>
  );
};

export default ChatInterface; 